/**
 * @file `useLearnerAnalytics` — ordered identify-then-pageview for the
 *       login-only lab surface.
 * @module @labcraft/lab/ui/hooks/use-learner-analytics
 *
 * The single unlock that makes a login-only product measurable in Umami
 * (umbrella #1213, Phase 2 / #1217): without an identify call every lab
 * session is an anonymous stranger, so no cohort / retention /
 * segmentation is possible despite every visitor being a known learner.
 * This hook reads the current learner from `useMe()` and calls
 * {@link identify} once per session, keying the Umami profile on the real
 * `learners.id` UUID so Umami's behavioral funnels reconcile to Postgres
 * over that same pseudonymous identifier.
 *
 * **Why this hook also owns pageviews (the shadow-session fix).** Umami's
 * tracker, when auto-track is on, records the *initial* pageview during
 * its own script execution — before any app code can call `identify()`.
 * That first pageview is therefore anonymous, and because Umami folds the
 * distinct ID into the session hash, a known learner ends up with TWO
 * session rows: a throwaway anonymous "shadow" holding the pre-identify
 * pageview, plus the real identified session. On a one-person day that
 * shows as 2 visitors. The fix is to disable auto-track
 * (`useAnalytics(config, { autoTrack: false })` at the call site) and let
 * this hook drive pageviews explicitly, in order: wait for `/me` to
 * settle, `identify()` the learner if there is one, and only THEN send
 * the pageview — so a known learner's first pageview already carries the
 * distinct ID and no anonymous shadow is ever opened. Anonymous visitors
 * (never sign in) still get pageviews, correctly attributed to a single
 * anonymous session. Auto-track's other job — SPA route pageviews — is
 * replaced here by re-firing on `useLocation()` changes.
 *
 * **PII invariant (umbrella-wide).** The distinct ID is the UUID (never
 * email / name — {@link identify} drops any non-UUID argument at the
 * wrapper boundary), and every session property is a bounded value:
 *
 *   - `plan` — a closed {@link Plan} tier (const `'free'` today; the type
 *     is a union so tiers can be added without a rename).
 *   - `cohortWeek` — a `YYYY-Www` bucket derived from `created_at` via the
 *     pure `CoreAnalytics.bucketCohortWeek`; the raw signup timestamp never
 *     reaches Umami, only the week bucket.
 *   - `acquisitionSource` — the granular acquisition source (a
 *     `CoreAnalytics`-sanitized safe string) captured at `/authorize`
 *     (#1214) and surfaced on `/me`, or `'unknown'` when none was captured.
 *   - `acquisitionCategory` — the derived coarse acquisition category
 *     (`campaign` | `organic` | `referral` | `direct`), or `'unknown'`.
 *   - `acquisitionCampaign` — the specific campaign name (a
 *     `CoreAnalytics`-sanitized safe string) captured from `utm_campaign`,
 *     or `'unknown'` when the visit carried no campaign tag.
 *
 * The closed {@link IdentifySession} record is what pins the invariant at
 * the call site: it has no `email` / `name` / `referrer` key, so no such
 * value can be passed, and each value type is a closed enum, a
 * regex-validated bucket, or a `CoreAnalytics`-sanitized safe string. The
 * dependency-agnostic `@labcraft/ui` wrapper enforces the mechanical floor
 * (UUID distinct ID, scalar values); this module supplies the bounded
 * ceiling using `@labcraft/core`'s shared analytics surface.
 *
 * **Idempotency.** identify must fire once per *session*, not once per
 * render, and each route gets exactly one pageview. A React effect re-runs
 * on every dependency change, and StrictMode double-invokes effects in
 * dev; naive calls would re-identify and double-count. Two refs guard it:
 * `identifiedIdRef` holds the last-identified distinct ID (identify fires
 * only on a new learner UUID), and `lastPageviewPathRef` holds the
 * last-tracked pathname (a pageview fires only when the path changes). So
 * re-renders, StrictMode's double-mount, and a same-learner `/me` refetch
 * are all no-ops; a learner change (sign-out → sign-in) legitimately
 * re-identifies with the new UUID.
 *
 * **Tracker readiness.** `useAnalytics` injects the tracker `<script>`
 * asynchronously, so `window.umami` may be absent when `/me` first
 * settles. {@link identify} / {@link trackPageview} no-op when the tracker
 * is absent, so the effect retries on a short interval until the tracker
 * is present, then fires once and stops. The poll is **bounded** — it
 * gives up after {@link MAX_TRACKER_POLLS} attempts (~10s at
 * {@link TRACKER_POLL_MS}) and clears the interval. This matters in the
 * default analytics-dark mode: with no config the tracker never loads, so
 * an unbounded poll would tick every 250ms for the whole session; the cap
 * turns that into a short, self-terminating probe.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import {
  CoreAnalytics,
  type AcquisitionCategory,
  type CohortWeek,
} from '@labcraft/core'
import { identify, trackPageview, type SessionData } from '@labcraft/ui/hooks'
import { useMe } from './use_me.ts'

/**
 * The learner's plan tier. A closed union rather than a bare `'free'`
 * literal so a paid tier can be added later (`'free' | 'paid'`) without
 * renaming the identify session-property or splitting historical Umami
 * data. Only `'free'` exists today.
 */
export type Plan = 'free'

/** The single plan tier every learner is on today. */
const CURRENT_PLAN: Plan = 'free'

/**
 * How often (ms) the effect re-checks for the async-loaded tracker before
 * firing. Short enough that identify + the pageview land promptly once the
 * `<script>` loads; the interval clears itself the moment it succeeds (or
 * on unmount), so it never runs unbounded.
 */
const TRACKER_POLL_MS = 250

/**
 * How many times the effect polls for the async-loaded tracker before
 * giving up. `40 × 250ms = ~10s` — long enough that a slow tracker script
 * still lands the identify, short enough that the interval self-terminates
 * instead of ticking for the whole session. Load-bearing in the default
 * analytics-dark mode, where the tracker never loads and an unbounded poll
 * would run forever.
 */
const MAX_TRACKER_POLLS = 40

/**
 * The closed session-identity payload for `umami.identify()`. Every value
 * is a bounded scalar — a closed-enum member ({@link Plan},
 * {@link AcquisitionCategory}), a regex-validated `YYYY-Www` cohort bucket,
 * or a `CoreAnalytics`-sanitized safe source string. The record has no
 * free-text key, which is the type-level enforcement of the umbrella's PII
 * invariant: there is no `email` / `name` / `referrer` field to populate,
 * so none can be sent.
 *
 * @property plan - The learner's plan tier.
 * @property cohortWeek - The `YYYY-Www` signup-week bucket (a branded
 *   {@link CohortWeek}, so a bare string can't be assigned here).
 * @property acquisitionSource - The granular acquisition source (a
 *   `CoreAnalytics`-sanitized safe string), or `'unknown'` for rows that
 *   predate capture (a bounded sentinel, never a raw value).
 * @property acquisitionCategory - The derived coarse acquisition category,
 *   or `'unknown'` when none was captured (a bounded sentinel).
 * @property acquisitionCampaign - The specific campaign name (a
 *   `CoreAnalytics`-sanitized safe string), or `'unknown'` when the visit
 *   carried no `utm_campaign` tag (a bounded sentinel).
 */
export type IdentifySession = {
  plan: Plan
  cohortWeek: CohortWeek
  acquisitionSource: string | 'unknown'
  acquisitionCategory: AcquisitionCategory | 'unknown'
  acquisitionCampaign: string | 'unknown'
}

/**
 * Drive Umami analytics for the lab surface: identify the current learner
 * (once per session) and send a pageview per route — always in that order,
 * so a known learner's pageview is never anonymous (no shadow session).
 * No-op while `/me` is still pending (identity unknown), while anonymous
 * for the identify step (a `null` learner still gets pageviews), and while
 * the tracker script has not loaded. Idempotent across re-renders,
 * StrictMode double-invocation, and same-learner `/me` refetches — see the
 * module header for the guards' shape.
 *
 * Requires auto-track to be OFF at the `useAnalytics` call site
 * (`useAnalytics(config, { autoTrack: false })`); otherwise Umami's own
 * initial pageview reintroduces the shadow this hook exists to prevent.
 *
 * Mount once inside the chromed shell (alongside `useAnalytics`); the hook
 * renders nothing and returns nothing.
 */
export function useLearnerAnalytics(): void {
  const me = useMe()
  const location = useLocation()
  const learner = me.data?.learner ?? null

  // The last distinct ID we forwarded to Umami. `null` until the first
  // identify lands. Guards re-identify: a run whose learner UUID matches is
  // a no-op.
  const identifiedIdRef = useRef<string | null>(null)

  // The last pathname we sent a pageview for. Guards against double-count:
  // effect re-runs (StrictMode, `/me` refetch) that don't change the path
  // don't re-fire the pageview; a real navigation does.
  const lastPageviewPathRef = useRef<string | null>(null)

  useEffect(() => {
    // Wait until `/me` settles. Sending a pageview while identity is still
    // pending is exactly what opens the anonymous shadow session — a known
    // learner would get an anonymous pageview before identify() runs. Once
    // settled we know learner-or-anonymous and can order the calls.
    if (me.isPending) return

    // Flatten the compound `acquisition` value object into two scalar
    // session props — `umami.identify()` takes scalar-only values. A null
    // `acquisition` (never captured) maps both props to the `'unknown'`
    // sentinel. `null` when anonymous: no identify, pageview only.
    const session: IdentifySession | null = learner
      ? {
          plan: CURRENT_PLAN,
          cohortWeek: CoreAnalytics.bucketCohortWeek(learner.createdAt),
          acquisitionSource: learner.acquisition?.source ?? 'unknown',
          acquisitionCategory: learner.acquisition?.category ?? 'unknown',
          acquisitionCampaign: learner.acquisition?.campaign ?? 'unknown',
        }
      : null

    // identify() (when a learner) MUST precede the pageview so the pageview
    // carries the distinct ID — the whole reason auto-track is disabled.
    // The tracker loads asynchronously; retry until `window.umami` exists.
    const fire = (): boolean => {
      if (!window.umami) return false

      if (learner && session && identifiedIdRef.current !== learner.id) {
        identify(learner.id, session satisfies SessionData)
        identifiedIdRef.current = learner.id
      }

      if (lastPageviewPathRef.current !== location.pathname) {
        trackPageview()
        lastPageviewPathRef.current = location.pathname
      }
      return true
    }

    if (fire()) return

    // Bound the poll: give up after MAX_TRACKER_POLLS attempts so the
    // interval self-terminates instead of ticking forever when the tracker
    // never loads (the default analytics-dark mode).
    let polls = 0
    const timer = window.setInterval(() => {
      polls += 1
      if (fire() || polls >= MAX_TRACKER_POLLS) window.clearInterval(timer)
    }, TRACKER_POLL_MS)
    return () => window.clearInterval(timer)
  }, [me.isPending, learner, location.pathname])
}
